import React from 'react';
import { difference } from 'lodash';

import { substringSearchFilter } from '@neptune/shared/search-business-logic';
import { QueryFilterSearch } from '@neptune/shared/search-domain';
import { searchTags } from '@neptune/shared/tags-domain';

export const TagsDataSourceContext = React.createContext<QueryFilterSearch<string, string>>(
  async () => ({
    entries: [],
  }),
);

type UseTagsDataSourceProps = {
  limit?: number;
  projectIdentifier: string;
  types: string[];
  disabledTags?: string[];
};

export const useProjectTagsDataSource = ({
  limit = 50,
  projectIdentifier,
  types,
  disabledTags,
}: UseTagsDataSourceProps): QueryFilterSearch<string, string> => {
  const serializedTypes = types && JSON.stringify(types);
  const rawQuery = React.useCallback(
    async (search: string, attributePath: string | undefined) => {
      return searchTags({
        projectIdentifier,
        query: '',
        search,
        type: serializedTypes && JSON.parse(serializedTypes),
        attributePath,
      });
    },
    [projectIdentifier, serializedTypes],
  );

  const prepareOutput = React.useCallback(
    (collection: string[], search: string) => {
      const filteredResults = disabledTags ? difference(collection, disabledTags) : collection;
      const allResults = substringSearchFilter(search, filteredResults);
      return allResults.slice(0, limit);
    },
    [disabledTags, limit],
  );

  return React.useCallback(
    async (search: string, attributePath: string | undefined) => {
      const collection = await rawQuery(search.trim(), attributePath);

      const entries = prepareOutput(collection, search.trim());
      return {
        entries,
      };
    },
    [prepareOutput, rawQuery],
  );
};
